import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft, FiUploadCloud, FiX } from "react-icons/fi";
import api, { imgUrl } from "../utils/api";
import Loader from "../components/Loader";

const CATEGORIES = [
  "Pooja Ceremony",
  "Wedding / Vivah",
  "Griha Pravesh",
  "Navchandi Yag",
  "Astrology Session",
  "Festival",
  "Other",
];

const emptyForm = {
  title: "",
  category: "Other",
  caption: "",
  images: [],
  featured: false,
};

export default function GalleryForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api
        .get(`/gallery/${id}`)
        .then((res) => setForm(res.data))
        .catch(() => toast.error("Failed to load gallery item"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("images", f));
    try {
      const { data } = await api.post("/upload/multiple", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({ ...form, images: [...form.images, ...data.urls] });
      toast.success("Images uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/gallery/${id}`, form);
        toast.success("Gallery item updated");
      } else {
        await api.post("/gallery", form);
        toast.success("Gallery item created");
      }
      navigate("/admin/gallery");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="max-w-3xl">
      <Link
        to="/admin/gallery"
        className="inline-flex items-center gap-1 text-saffron-600 text-sm font-medium mb-6 hover:gap-2 transition-all"
      >
        <FiArrowLeft /> Back to Gallery
      </Link>

      <h1 className="font-heading text-2xl text-maroon-800 mb-6">
        {isEdit ? "Edit Gallery Item" : "Add New Gallery Item"}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
            placeholder="e.g. Satyanarayan Mahapuja at Thane West"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            name="category"
            value={form.category}
            onChange={handleChange}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-white"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
          <textarea
            name="caption"
            value={form.caption}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
            placeholder="Brief description of the event..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Photos</label>
          <div className="flex flex-wrap gap-3 mb-3">
            {form.images.map((img, idx) => (
              <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                <img src={imgUrl(img)} alt={`gallery ${idx}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5"
                >
                  <FiX size={12} />
                </button>
              </div>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
            <FiUploadCloud /> {uploading ? "Uploading..." : "Upload Photos (up to 6)"}
            <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} className="w-4 h-4" />
          Featured (shown on homepage gallery section)
        </label>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-maroon-700 hover:bg-maroon-800 disabled:opacity-60 text-gold-100 px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          <FiSave /> {saving ? "Saving..." : "Save Gallery Item"}
        </button>
      </form>
    </div>
  );
}
